import {ChartType, ChartTypeDisplayName, ChartVizType} from 'common/report/constants';
import type {SectionItem, TooltipInfo} from 'common/components/items-menu/types';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import barSvg from 'common/assets/images/charts/bar.svg';
import lineSvg from 'common/assets/images/charts/line.svg';
import metricSvg from 'common/assets/images/charts/metric.svg';
import pieSvg from 'common/assets/images/charts/pie.svg';
import stackedBarSvg from 'common/assets/images/charts/stacked-bar.svg';
import stackedLineSvg from 'common/assets/images/charts/stacked-line.svg';
import stackedVerticalBarSvg from 'common/assets/images/charts/stacked-vertical-bar.svg';
import tableSvg from 'common/assets/images/charts/table.svg';
import funnelFrequencySvg from 'common/assets/images/charts/funnel-frequency.svg';
import funnelStepsSvg from 'common/assets/images/charts/funnel-steps.svg';
import funnelTtcSvg from 'common/assets/images/charts/funnel-ttc.svg';
import funnelTrendsSvg from 'common/assets/images/charts/funnel-trends.svg';
import sankeySvg from 'common/assets/images/charts/sankey.svg';
import retentionTableSvg from 'common/assets/images/charts/retention-table.svg';
import retentionTrendsSvg from 'common/assets/images/charts/retention-trends.svg';
import verticalBarSvg from 'common/assets/images/charts/vertical-bar.svg';

const commonTooltipAttrs: TooltipInfo = {
  size: DisplaySize.Small,
  placement: Placement.Right,
};

export const CHART_VIZ_TYPE_ICONS: {[key in ChartVizType]: string} = {
  [ChartVizType.Bar]: `bar`,
  [ChartVizType.Line]: `line`,
  [ChartVizType.Metric]: `single-metric`,
  [ChartVizType.Steps]: `funnel-steps`,
  [ChartVizType.Sankey]: `top-paths`,
};

export type ChartGroup = 'series' | 'bar' | 'standard' | 'special';
export const ChartTypeSelection: Partial<{[key in ChartType]: SectionItem<ChartType, {chartGroup: ChartGroup}>}> = {
  [ChartType.Bar]: {
    icon: `bar`,
    label: ChartTypeDisplayName[ChartType.Bar],
    value: ChartType.Bar,
    tooltip: {
      text: `Bars are best to display comparisons between categories.`,
      'image-src': barSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `bar`,
  },
  [ChartType.VerticalBar]: {
    icon: `vertical-bar`,
    label: ChartTypeDisplayName[ChartType.VerticalBar],
    value: ChartType.VerticalBar,
    tooltip: {
      text: `Vertical bar charts display trends over time.`,
      'image-src': verticalBarSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `series`,
  },
  [ChartType.StackedVerticalBar]: {
    icon: `vertical-bar-stacked`,
    label: ChartTypeDisplayName[ChartType.StackedVerticalBar],
    value: ChartType.StackedVerticalBar,
    tooltip: {
      text: `Stacked vertical bar charts display compositions of counts over time.`,
      'image-src': stackedVerticalBarSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `series`,
  },
  [ChartType.Line]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.Line],
    value: ChartType.Line,
    tooltip: {
      text: `Line charts display trends over time.`,
      'image-src': lineSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `series`,
  },
  [ChartType.Pie]: {
    icon: `pie`,
    label: ChartTypeDisplayName[ChartType.Pie],
    value: ChartType.Pie,
    tooltip: {
      text: `Pie charts illustrate composition of a whole in pie form.`,
      'image-src': pieSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `standard`,
  },
  [ChartType.Metric]: {
    icon: `single-metric`,
    label: ChartTypeDisplayName[ChartType.Metric],
    value: ChartType.Metric,
    tooltip: {
      text: `Metric chart displays a single value for each metric.`,
      'image-src': metricSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `standard`,
  },
  [ChartType.StackedBar]: {
    icon: `bar-stacked`,
    label: ChartTypeDisplayName[ChartType.StackedBar],
    value: ChartType.StackedBar,
    tooltip: {
      text: `Stacked bar is best when comparing composition of multiple counts.`,
      'image-src': stackedBarSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `bar`,
  },
  [ChartType.StackedLine]: {
    icon: `stacked-line`,
    label: ChartTypeDisplayName[ChartType.StackedLine],
    value: ChartType.StackedLine,
    tooltip: {
      text: `Stacked line charts display compositions of counts over time.`,
      'image-src': stackedLineSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `series`,
  },
  [ChartType.Sankey]: {
    icon: `top-paths`,
    label: ChartTypeDisplayName[ChartType.Sankey],
    value: ChartType.Sankey,
    chartGroup: `special`,
  },
  [ChartType.Table]: {
    icon: `table`,
    label: ChartTypeDisplayName[ChartType.Table],
    value: ChartType.Table,
    tooltip: {
      text: `Tables display data in a tabular view.`,
      'image-src': tableSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `standard`,
  },
  [ChartType.FunnelSteps]: {
    icon: `funnel-steps`,
    label: ChartTypeDisplayName[ChartType.FunnelSteps],
    value: ChartType.FunnelSteps,
    tooltip: {
      text: `View conversion rates between funnel steps.`,
      'image-src': funnelStepsSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.FunnelTrend]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.FunnelTrend],
    value: ChartType.FunnelTrend,
    tooltip: {
      text: `View changes in conversion rate over time.`,
      'image-src': funnelTrendsSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.FunnelFrequencyLine]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.FunnelFrequencyLine],
    value: ChartType.FunnelFrequencyLine,
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.FunnelFrequencyBar]: {
    icon: `bar`,
    label: ChartTypeDisplayName[ChartType.FunnelFrequencyBar],
    value: ChartType.FunnelFrequencyBar,
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.FunnelTTCBar]: {
    icon: `bar`,
    label: ChartTypeDisplayName[ChartType.FunnelTTCBar],
    value: ChartType.FunnelTTCBar,
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.FunnelTTCLine]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.FunnelTTCLine],
    value: ChartType.FunnelTTCLine,
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.FunnelMedianTTC]: {
    icon: `bar-stacked`,
    label: ChartTypeDisplayName[ChartType.FunnelMedianTTC],
    value: ChartType.FunnelMedianTTC,
    tooltip: {
      text: `View a distribution of the time it takes users to convert through the funnel.`,
      'image-src': funnelTtcSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.RetentionLine]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.RetentionLine],
    value: ChartType.RetentionLine,
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.RetentionTrend]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.RetentionTrend],
    value: ChartType.RetentionTrend,
    tooltip: {
      text: `View the retention trend over a period of time.`,
      'image-src': retentionTrendsSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.RetentionTrendMetric]: {
    icon: `single-metric`,
    // use InsightMetric here, we will eventually use insight API so this is correct
    label: ChartTypeDisplayName[ChartType.Metric],
    value: ChartType.RetentionTrendMetric,
    tooltip: {
      text: `View the retention trend over a period of time.`,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  [ChartType.RetentionTable]: {
    icon: `retention-curve`,
    label: ChartTypeDisplayName[ChartType.RetentionTable],
    value: ChartType.RetentionTable,
    tooltip: {
      text: `View retention rates of individual cohorts of users.`,
      'image-src': retentionTableSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  [ChartType.RetentionCurve]: {
    icon: `retention-curve`,
    label: ChartTypeDisplayName[ChartType.RetentionTable],
    value: ChartType.RetentionCurve,
    tooltip: {
      text: `View retention rates of individual cohorts of users.`,
      'image-src': retentionTableSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  [ChartType.Frequency]: {
    icon: `bar`,
    label: ChartTypeDisplayName[ChartType.Frequency],
    value: ChartType.Frequency,
    tooltip: {
      text: `The number of times a user completes a step before converting or dropping off.`,
      'image-src': funnelFrequencySvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  [ChartType.FunnelTopPaths]: {
    icon: `top-paths`,
    label: ChartTypeDisplayName[ChartType.FunnelTopPaths],
    value: ChartType.FunnelTopPaths,
    tooltip: {
      text: `When using an Any Order Funnel, view which paths were most common.`,
      'image-src': sankeySvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  [ChartType.FlowsTopPaths]: {
    icon: `funnel-steps`,
    label: ChartTypeDisplayName[ChartType.FlowsTopPaths],
    value: ChartType.FlowsTopPaths,
    tooltip: {
      text: `Specifically analyze the most often followed sequences of events.`,
      'image-src': funnelStepsSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  [ChartType.Flows]: {
    icon: `top-paths`,
    label: ChartTypeDisplayName[ChartType.Flows],
    value: ChartType.Sankey, // Alias
    tooltip: {
      text: `Explore a fluid diagram of various user paths.`,
      'image-src': sankeySvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  [ChartType.ImpactAdoption]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.ImpactAdoption],
    value: ChartType.ImpactAdoption,
    chartGroup: `special`,
  },
  [ChartType.ImpactTrends]: {
    icon: `stacked-line`,
    label: ChartTypeDisplayName[ChartType.ImpactTrends],
    value: ChartType.ImpactTrends,
    chartGroup: `special`,
  },
  [ChartType.ImpactPropensity]: {
    icon: `bar-stacked`,
    label: ChartTypeDisplayName[ChartType.ImpactPropensity],
    value: ChartType.ImpactPropensity,
    chartGroup: `special`,
  },
};
